import { observable, configure, action, computed } from 'mobx';

import util from 'preact-util';
import { route } from 'preact-router';
import PubSub, { topics } from '../lib/pubsub';

configure({ enforceActions: 'always' });

class VippsStore {
    constructor() {

    }

    @action
    updateKeyValue(key, value) {
        this[key] = value;
    }

    @action
    updateObjectKeyValue(obj, key, value) {
        this[obj][key] = value;
    }

    @observable redirectUrl = null;

    async requestPayment(params = {}) {
        if (!params.amount || !params.title) {
            console.error('vippsStore.requestPayment', 'Missing amount and/or title');
            return;
        }
        const response = await util.fetchApi(`/api/vipps/request-payment`, { method: 'POST' }, {
            ...params,
        });
        switch (response.status) {
            case 200:
                // console.log('vippsStore.requestPayment', response);
                this.updateKeyValue('redirectUrl', response.data.redirectUrl);
                return response;
            default:
                console.error('vippsStore.requestPayment', response);
                return response;
        }
    }

    async getPaymentStatus(uuidv4) {
        const response = await util.fetchApi(`/api/vipps/payment-status/${uuidv4}`);
        switch (response.status) {
            case 200:
                // console.log('vippsStore.getPaymentStatus', response);
                return response;
            default:
                console.error('vippsStore.getPaymentStatus', response);
                return response;
        }
    }

    async paymentCapture(uuidv4) {
        const response = await util.fetchApi(`/api/vipps/payment-capture/${uuidv4}`);
        switch (response.status) {
            case 200:
                // console.log('vippsStore.paymentCapture', response);
                return response;
            default:
                console.error('vippsStore.paymentCapture', response);
                return response;
        }
    }

    async createCheckoutSession(params = {}) {
        if (!params.amount || !params.title) {
            console.error('vippsStore.createCheckoutSession', 'Missing amount and/or title');
            return;
        }
        const response = await util.fetchApi('/api/vipps/create-checkout-session', { method: 'POST' }, {
            ...params,
        });
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('vippsStore.createCheckoutSession', response);
                return response;
        }
    }

    async getCheckoutSessionStatus(uuidv4) {
        const response = await util.fetchApi(`/api/vipps/checkout-session-status/${uuidv4}`);
        switch (response.status) {
            case 200:
                return response;
            default:
                console.error('vippsStore.getCheckoutSessionStatus', response);
                return response;
        }
    }

    async paymentCancel(uuidv4) {
        const response = await util.fetchApi(`/api/vipps/payment-cancel/${uuidv4}`);
        switch (response.status) {
            case 200:
                // console.log('vippsStore.paymentCancel', response);
                return response;
            default:
                console.error('vippsStore.paymentCancel', response);
                return response;
        }
    }

    /**
     * Poll payment status until it's completed or timeout
     * @param {string} uuidv4 - The payment UUID
     * @param {Function} onUpdate - Callback on each poll iteration with current status
     * @param {Function} onComplete - Callback when payment is completed (status 2=AUTHORIZED or 3=CAPTURED)
     * @param {Function} onFailed - Callback when payment failed/cancelled (status >= 90)
     * @param {number} maxAttempts - Maximum number of polling attempts (default: 60)
     * @param {number} interval - Polling interval in milliseconds (default: 2000)
     * @param {Function} getStatus - Optional replacement for getPaymentStatus (owner-side routes).
     * @returns {Promise<void>}
     */
    @action
    async pollPaymentStatus(uuidv4, {
        onUpdate, onComplete, onFailed, maxAttempts = 60, interval = 2000, getStatus,
    } = {}) {
        let attempts = 0;
        const fetchStatus = typeof getStatus === 'function' ? getStatus : id => this.getPaymentStatus(id);

        const poll = async () => {
            attempts++;

            if (attempts > maxAttempts) {
                console.log(`[VippsStore] Max polling attempts reached for ${uuidv4}`);
                if (onFailed) {
                    onFailed({ timeout: true, attempts });
                }
                return;
            }

            const response = await fetchStatus(uuidv4);

            // Call onUpdate callback with current status (for UI updates)
            if (onUpdate && response?.data) {
                onUpdate(response.data);
            }

            if (response?.data?.paymentStatus === 2 || response?.data?.paymentStatus === 3) {
                // Payment completed (2 = AUTHORIZED, 3 = CAPTURED)
                console.log(`[VippsStore] Payment ${uuidv4} completed (status: ${response.data.paymentStatus}) after ${attempts} attempts`);
                console.log('[VippsStore] onComplete callback exists:', !!onComplete);
                if (onComplete) {
                    console.log('[VippsStore] Calling onComplete with:', response.data);
                    onComplete(response.data);
                    console.log('[VippsStore] onComplete called (async - not waiting for completion)');
                }
                return;
            }

            if (response?.data?.paymentStatus >= 90) {
                // Payment failed/cancelled
                console.log(`[VippsStore] Payment ${uuidv4} failed/cancelled (status: ${response.data.paymentStatus})`);
                if (onFailed) {
                    onFailed(response.data);
                }
                return;
            }

            if (response?.status === 404) {
                // Payment is no longer pending (cancelled/aborted/expired or removed). Stop polling
                // so the UI doesn't hang on "checking" until the max-attempts timeout.
                console.log(`[VippsStore] Payment ${uuidv4} not pending (404) — treating as cancelled`);
                if (onFailed) {
                    onFailed({ cancelled: true });
                }
                return;
            }

            // Continue polling
            setTimeout(poll, interval);
        };

        poll();
    }
}

const store = new VippsStore();
export default store;
